import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { StepHeader, OnboardingButton, OnboardingInput } from '../components'
import { useOnboardingStore } from '@/stores/onboardingStore'
import { ArrowRightIcon } from '@untitledui-icons/react/line'

interface NameStepProps {
  onNext: () => void
  onBack: () => void
}

export function NameStep({ onNext, onBack }: NameStepProps) {
  const { t } = useTranslation()
  const { firstName, lastName, setName } = useOnboardingStore()

  const schema = z.object({
    firstName: z.string().min(1, t('resources.signup.name.first_name_required')),
    lastName: z.string().min(1, t('resources.signup.name.last_name_required')),
  })

  type FormData = z.infer<typeof schema>

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { firstName, lastName },
  })

  const onSubmit = (data: FormData) => {
    setName(data.firstName, data.lastName)
    onNext()
  }

  return (
    <div className="flex w-full flex-1 flex-col">
      <StepHeader
        title={t('resources.signup.name.title')}
        subtitle={t('resources.signup.name.subtitle')}
        onBack={onBack}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-1 flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <OnboardingInput
            {...register('firstName')}
            label={t('resources.signup.name.first_name')}
            placeholder={t('resources.signup.name.first_name')}
            error={errors.firstName?.message}
          />

          <OnboardingInput
            {...register('lastName')}
            label={t('resources.signup.name.last_name')}
            placeholder={t('resources.signup.name.last_name')}
            error={errors.lastName?.message}
          />
        </div>

        <div className="mt-auto pt-4 lg:mt-0 lg:pt-0">
          <OnboardingButton type="submit" iconTrailing={ArrowRightIcon}>
            {t('resources.signup.common.get_started')}
          </OnboardingButton>
        </div>
      </form>
    </div>
  )
}
